<!-- ── Hero ── -->
<section class="hero">
  <div class="hero-inner">
    <div class="hero-left">
      <h1>Create New Request</h1>
      <p class="hero-sub">Fill in the details below to submit a new request</p>
    </div>
  </div>
</section>

<div class="form-shell">

  <!-- Success -->
  <div *ngIf="success" class="status-bar success">
    <mat-icon>check_circle</mat-icon> Request created successfully! Redirecting…
  </div>

  <!-- Error -->
  <div *ngIf="error" class="status-bar error">
    <mat-icon>error</mat-icon> {{ error }}
  </div>

  <!-- Upload progress -->
  <div *ngIf="uploadProgress" class="status-bar info">
    <mat-icon class="spinner">hourglass_empty</mat-icon> Uploading file…
  </div>

  <!-- Form Card -->
  <div class="card">
    <div class="card-header">
      <mat-icon>post_add</mat-icon>
      <h3>Request Details</h3>
    </div>
    <div class="card-body">
      <form (ngSubmit)="createRequest()" #rfiForm="ngForm">

        <!-- ── Section: Core Details ── -->
        <div class="form-section">
          <div class="section-label">
            <mat-icon>assignment</mat-icon>
            <span>Core Details</span>
          </div>
          <div class="form-grid">
            <!-- Project -->
            <div class="field span-half">
              <label for="project-select">Project</label>
              <p class="field-hint">Select the project this request belongs to</p>
              <div class="input-wrap select-wrap">
                <mat-icon class="input-icon">business</mat-icon>
                <select
                  id="project-select"
                  [(ngModel)]="request.projectNumber"
                  (ngModelChange)="projectSelected()"
                  name="projectNumber"
                  required>
                  <option [ngValue]="">-- Select a project --</option>
                  <option *ngFor="let project of projects" [ngValue]="project.projectNumber.toString()">
                    {{ project.projectNumber }} - {{ project.projectName }}
                  </option>
                </select>
              </div>
              <div *ngIf="projectsLoading" class="loading-hint">
                <mat-icon class="spinner">hourglass_empty</mat-icon> Loading projects…
              </div>
            </div>

            <!-- Request Type -->
            <div class="field span-half">
              <label for="request-type">Request Type</label>
              <p class="field-hint">Choose the category for this request</p>
              <div class="input-wrap select-wrap">
                <mat-icon class="input-icon">category</mat-icon>
                <select
                  id="request-type"
                  name="requestType"
                  [(ngModel)]="request.requestType"
                  [disabled]="limitedToIq"
                  required>
                  <option *ngFor="let type of allowedRequestTypes" [ngValue]="type">{{ type }}</option>
                </select>
              </div>
            </div>

            <!-- Request Title -->
            <div class="field span-full">
              <label for="description">Request Title</label>
              <p class="field-hint">Briefly describe what this request is about</p>
              <div class="input-wrap">
                <mat-icon class="input-icon">title</mat-icon>
                <input
                  type="text"
                  id="description"
                  name="description"
                  [(ngModel)]="request.description"
                  maxlength="100"
                  required
                  placeholder="Enter a request title"
                />
              </div>
              <div class="char-counter" [class.at-limit]="request.description.length >= 100">
                {{ request.description.length }} / 100
              </div>
            </div>

            <!-- Priority -->
            <div class="field span-third">
              <label for="priority">Priority</label>
              <p class="field-hint">Priority level</p>
              <div class="input-wrap select-wrap">
                <mat-icon class="input-icon">flag</mat-icon>
                <select
                  id="priority"
                  name="priority"
                  [(ngModel)]="request.priority">
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>

            <!-- Area -->
            <div class="field span-third">
              <label for="area">Area</label>
              <p class="field-hint">Optional</p>
              <div class="input-wrap">
                <mat-icon class="input-icon">place</mat-icon>
                <input
                  type="text"
                  id="area"
                  name="area"
                  [(ngModel)]="request.area"
                  maxlength="25"
                  #areaField="ngModel"
                  placeholder="e.g. Building A"
                />
              </div>
              <div class="char-counter" [class.at-limit]="request.area.length >= 25">
                {{ request.area.length }} / 25
              </div>
            </div>

            <!-- Sequence -->
            <div class="field span-third">
              <label for="sequence">Sequence</label>
              <p class="field-hint">Optional</p>
              <div class="input-wrap">
                <mat-icon class="input-icon">format_list_numbered</mat-icon>
                <input
                  type="text"
                  id="sequence"
                  name="sequence"
                  [(ngModel)]="request.sequence"
                  maxlength="100"
                  #sequenceField="ngModel"
                  placeholder="e.g. Phase 2 - Step 3"
                />
              </div>
              <div class="char-counter" [class.at-limit]="request.sequence.length >= 100">
                {{ request.sequence.length }} / 100
              </div>
            </div>
          </div>
        </div>

        <!-- ── Section: Revision & Attachments ── -->
        <div class="form-section">
          <div class="section-label">
            <mat-icon>description</mat-icon>
            <span>Revision &amp; Attachments</span>
          </div>
          <div class="form-grid">
            <!-- Revision Text -->
            <div class="field span-full">
              <label for="revisionText">Revision Text</label>
              <p class="field-hint">Enter the question or details for Revision 0</p>
              <div class="input-wrap textarea-wrap">
                <mat-icon class="input-icon textarea-icon">question_answer</mat-icon>
                <textarea
                  id="revisionText"
                  name="revisionText"
                  [(ngModel)]="revisionText"
                  required
                  placeholder="Enter the revision question or details"
                  rows="4"
                ></textarea>
              </div>
            </div>

            <!-- Attachments -->
            <div class="field span-full">
              <label>Attachments</label>
              <p class="field-hint">Optionally attach files to this revision</p>
              <div class="file-upload-area"
                   [class.drag-over]="isDragOver"
                   (click)="fileInput.click()"
                   (dragover)="onDragOver($event)"
                   (dragleave)="onDragLeave($event)"
                   (drop)="onFileDrop($event)">
                <mat-icon>cloud_upload</mat-icon>
                <span>Drag &amp; drop files here, or click to browse</span>
                <input
                  #fileInput
                  type="file"
                  id="fileUpload"
                  name="fileUpload"
                  (change)="onFileSelected($event)"
                  multiple
                  hidden
                />
              </div>
              <div class="file-list" *ngIf="selectedFiles.length > 0">
                <div class="file-list-item" *ngFor="let f of selectedFiles; let i = index">
                  <mat-icon class="file-list-icon">insert_drive_file</mat-icon>
                  <span class="file-list-name">{{ f.name }}</span>
                  <button type="button" class="file-remove-btn" (click)="removeFile(i)" title="Remove file">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Status pill -->
        <div class="status-pill-row">
          <mat-icon>info_outline</mat-icon>
          <span>Status: <strong>Open</strong></span>
          <span class="status-pill-hint">All new requests start as Open</span>
        </div>

        <div class="form-actions">
          <a class="btn-cancel" routerLink="/requests">Cancel</a>
          <button type="submit" class="btn-primary" [disabled]="loading || !rfiForm.valid || !request.projectNumber">
            <mat-icon *ngIf="!loading">add</mat-icon>
            <mat-icon *ngIf="loading" class="spinner">hourglass_empty</mat-icon>
            {{ loading ? 'Creating…' : 'Create Request' }}
          </button>
        </div>

      </form>
    </div>
  </div>

</div>